import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Eye, BookOpen, FileText, MessageCircle, Send } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { infantilAPI } from '../services/api';

const API_URL = window.location.origin || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };
const FIELD_COLORS = { 'bncc-ei-01': '#8E52AB', 'bncc-ei-02': '#F59E0B', 'bncc-ei-03': '#EC4899', 'bncc-ei-04': '#3B82F6', 'bncc-ei-05': '#10B981' };

export default function FamilyPublishPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [published, setPublished] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [parentName, setParentName] = useState('');

  useEffect(() => { infantilAPI.classes().then(setClasses); }, []);
  useEffect(() => { if (selectedClass) infantilAPI.classStudents(selectedClass).then(setStudents); }, [selectedClass]);
  useEffect(() => {
    if (!selectedStudent) return;
    fetch(`${API_URL}/api/infantil360/family/published?student_id=${selectedStudent}&trimester=${encodeURIComponent('1º Trimestre')}`, { headers: headers() })
      .then(r => r.json()).then(setPublished);
    fetch(`${API_URL}/api/infantil360/family/feedback?student_id=${selectedStudent}`, { headers: headers() })
      .then(r => r.json()).then(setFeedbacks);
  }, [selectedStudent]);

  const handleFeedback = async () => {
    if (!newComment.trim() || !parentName.trim()) { toast.error('Preencha nome e comentário'); return; }
    try {
      await fetch(`${API_URL}/api/infantil360/family/feedback`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ student_id: selectedStudent, parent_name: parentName, comment: newComment })
      });
      setNewComment('');
      toast.success('Feedback enviado!');
      const updated = await fetch(`${API_URL}/api/infantil360/family/feedback?student_id=${selectedStudent}`, { headers: headers() }).then(r => r.json());
      setFeedbacks(updated);
    } catch { toast.error('Erro'); }
  };

  const obs = published?.observations || [];
  const portfolios = published?.portfolios || [];
  const reports = published?.reports || [];

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      data-testid="family-page">
      <motion.div variants={fadeUp}>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Heart className="h-5 w-5 text-[#EC4899]" /> DNA Connect — Família</h1>
        <p className="text-sm text-slate-500 mt-1">Visualize o que foi compartilhado com as famílias</p>
      </motion.div>

      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Turma</Label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setSelectedStudent(''); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Criança</Label>
            <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </motion.div>

      {selectedStudent && published && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Published Observations */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Eye className="h-4 w-4 text-[#64327D]" /> Observações ({obs.length})</h3>
            {obs.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nenhuma observação publicada para família</p> : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {obs.map((o, i) => (
                  <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-xs font-medium text-slate-700">{o.title}</p>
                    <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">{o.observation_text}</p>
                    {o.child_speech && <p className="text-[10px] italic text-[#64327D] mt-1">"{o.child_speech}"</p>}
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Portfolios + Reports */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4 text-[#F59E0B]" /> Portfólios ({portfolios.length})</h3>
            {portfolios.length === 0 ? <p className="text-xs text-slate-400 text-center py-2">Nenhum publicado</p> : portfolios.map((p, i) => (
              <div key={i} className="p-3 rounded-xl bg-amber-50 border border-amber-100 mb-2">
                <p className="text-xs font-medium text-amber-800">{p.title || 'Portfólio'}</p>
                <p className="text-[10px] text-amber-600">{p.item_ids?.length || 0} evidências</p>
              </div>
            ))}
            <h3 className="text-sm font-bold text-slate-800 mt-4 mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-[#EC4899]" /> Relatórios ({reports.length})</h3>
            {reports.length === 0 ? <p className="text-xs text-slate-400 text-center py-2">Nenhum aprovado</p> : reports.map((r, i) => (
              <div key={i} className="p-3 rounded-xl bg-pink-50 border border-pink-100 mb-2">
                <p className="text-xs text-pink-800 line-clamp-3">{r.content?.substring(0, 150)}...</p>
              </div>
            ))}
          </motion.div>

          {/* Feedback */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#2BB673]" /> Feedback da Família</h3>
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {feedbacks.length === 0 ? <p className="text-xs text-slate-400 text-center py-2">Nenhum feedback recebido</p> : feedbacks.map((f, i) => (
                <div key={i} className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-xs font-medium text-emerald-800">{f.parent_name}</p>
                  <p className="text-[10px] text-emerald-700 mt-0.5">{f.comment}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2 pt-3 border-t border-slate-100">
              <Input placeholder="Nome do responsável" value={parentName} onChange={e => setParentName(e.target.value)} className="rounded-xl text-xs h-9" />
              <div className="flex gap-2">
                <Input placeholder="Escreva um feedback..." value={newComment} onChange={e => setNewComment(e.target.value)} className="rounded-xl text-xs h-9 flex-1" />
                <Button size="sm" onClick={handleFeedback} className="rounded-xl h-9" style={{ background: '#2BB673' }}>
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
